import React, { PureComponent } from 'react';
import type { SyntheticEvent, ReactNode } from 'react';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';

import LimitationsNotification
  from 'components/pages/manage-family-page/limitations-notification/limitations-notification';
import {
  inviteFamilyMember,
  fetchFamilyPlan,
  removeFamilyMember,
  resendInvite,
} from 'state/family-plan/family-plan-actions';
import { fetchPricePlans } from 'state/price-plans/price-plans-actions';
import { fetchAllSubscriptions } from 'state/subscription/subscription-actions';
import { showDangerNotification, showSuccessNotification } from 'state/notifier/notifier-reducer';
import {
  getPricePlansList,
  getFamilyMembersExceptCurrent,
  isFamilyFull,
  getFamilyPlan,
  getFeatureFlags,
  getPrimarySubscription,
} from 'state/root-reducer';
import { showModal } from 'state/modal/modal-reducer';

import AppLayoutLoading from 'components/layout/app-layout/app-layout-loading';

import globalMessages from '../../../global-messages';

import FamilyMembersList from './family-members-list/family-members-list';

import './manage-family.scss';

import InviteIcon from './images/icons-invite.svg';

/* istanbul ignore next */
const mapStateToProps = (state) => ({
  primarySubscription: getPrimarySubscription(state),
  pricePlansList: getPricePlansList(state),
  familyMembers: getFamilyMembersExceptCurrent(state),
  featureFlags: getFeatureFlags(state),
  isFamilyFull: isFamilyFull(state),
  isFamilyProcessing: getFamilyPlan(state).isLoading,
});

const mapActionsToProps = {
  inviteFamilyMember,
  showDangerNotification,
  showSuccessNotification,
  fetchPricePlans,
  fetchFamilyPlan,
  fetchAllSubscriptions,
  removeFamilyMember,
  resendInvite,
  showModal,
};

const connector = connect(mapStateToProps, mapActionsToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  intl: IntlShape;
};

type State = {
  fields: {
    email: string;
  };
  fieldsErrors: {
    email?: ReactNode;
  };
};

class ManageFamilyPage extends PureComponent<Props, State> {
  state: State = {
    fields: {
      email: '',
    },
    fieldsErrors: {
      email: '',
    },
  };

  componentDidMount() {
    const {
      fetchPricePlans,
      fetchFamilyPlan,
      fetchAllSubscriptions,
    } = this.props;

    return Promise.all([
      fetchPricePlans(),
      fetchFamilyPlan(),
      fetchAllSubscriptions(),
    ])
      .catch(() => {
        const { showDangerNotification, intl } = this.props;

        showDangerNotification(intl.formatMessage(globalMessages.defaultError));
      });
  }

  render() {
    const {
      pricePlansList,
      familyMembers,

      primarySubscription,
    } = this.props;

    if (!pricePlansList.length || !primarySubscription) {
      return <AppLayoutLoading />;
    }

    if (!primarySubscription.pricePlan || !primarySubscription.pricePlan.features) {
      return null;
    }

    const { macSeatsCount = 0, iosSeatsCount = 0, maxMembersCount = 0 } = primarySubscription.pricePlan.features;

    return (
      <>
        <div className="manage-family-page__hero">
          <div>
            <h3 className="mb-5 mt-0">
              <FormattedMessage id="manageFamily.title" defaultMessage="Manage your Setapp Family" />
            </h3>

            <LimitationsNotification />

            <p className="mb-10">
              <br />
              <FormattedMessage
                id="manageFamily.description.devices"
                defaultMessage="Each participant can use Setapp on {macSeatsCount, plural, one {1 Mac} other {# Macs}}{iosSeatsCount, plural, =0 {} one {\u00A0+ 1 iOS device} other {\u00A0+ # iOS devices}}."
                values={{
                  macSeatsCount,
                  iosSeatsCount,
                }}
              />
            </p>
          </div>
        </div>
        <div className="manage-family-page__invites">
          {familyMembers.length ? (
            <div className="mt-10">
              <FamilyMembersList
                members={familyMembers}
                maxMembersCount={maxMembersCount}
              />
            </div>
          ) : (
            <div className="mt-5 text_color-secondary manage-family-page__invites-empty">
              <img className="mr-2" src={InviteIcon} width={20} height={20} alt="" />
              <FormattedMessage id="manageFamily.invites.empty" defaultMessage="You haven't invited any participants yet" />
            </div>
          )}
        </div>
      </>
    );
  }

  onInviteEmailChange = (e: SyntheticEvent<HTMLInputElement>) => {
    this.setState({ fields: { email: e.currentTarget.value } });
  };
}

export { ManageFamilyPage as PureManageFamilyPage };

export default connector(injectIntl(ManageFamilyPage));
